import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';

import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBarEntry } from '@nucleus-commerce/admin-kit/save-bar';
import type { Media } from '@nucleus-commerce/api-client/admin';

import {
  featuredFirst,
  sameGallery,
  toPutBody,
  type GalleryItem,
} from '../components/product-media-gallery';

import { useProductMedia, useSetProductMedia } from './use-product-media';

export interface ProductGalleryDraft {
  productId: string;
  query: ReturnType<typeof useProductMedia>;
  /** What the editor shows: the unsaved draft, or the saved gallery when there is none. */
  items: GalleryItem[];
  /** `items` as of the last change, for handlers that run before the next render. */
  latest: RefObject<GalleryItem[]>;
  change: (next: GalleryItem[]) => void;
  /** Appends files not in the gallery yet (uploads, library picks), for every variant. */
  add: (media: Media[]) => void;
  dirty: boolean;
  saving: boolean;
  save: () => Promise<void>;
  discard: () => void;
}

export interface ProductGalleryDraftOptions {
  /** After a save lands, so a page can refresh its own copy of the featured image. */
  onSaved?: () => void;
}

/**
 * The product gallery as an unsaved draft. Reorders, removals, additions and variant scopes stay
 * local until the page saves: the draft joins the save bar, so its Save, Ctrl+S, Discard and the
 * leave-page guard cover it next to the page's form. The page owns the draft, not the gallery
 * card, so a form that remounts after its own save does not drop the gallery's edits.
 */
export function useProductGalleryDraft(
  productId: string,
  featuredMediaId: string | null | undefined,
  { onSaved }: ProductGalleryDraftOptions = {},
): ProductGalleryDraft {
  const { can } = usePermissions();
  const query = useProductMedia(productId);
  const { mutateAsync, isPending: saving } = useSetProductMedia(productId);

  // Once this editor has saved, the server order is the truth (every save features the first
  // file); before that, an older product that features another file shows it first.
  const [saved, setSaved] = useState(false);
  const server = useMemo(() => {
    const rows = (query.data ?? []).map((row) => ({
      mediaId: row.mediaId,
      variantIds: row.variantIds,
      media: row.media,
    }));
    return saved ? rows : featuredFirst(rows, featuredMediaId);
  }, [query.data, featuredMediaId, saved]);

  const [draft, setDraft] = useState<GalleryItem[] | null>(null);
  // Files edited in the details sheet refetch the gallery; the draft shows their new alt text.
  const items = useMemo(() => {
    if (!draft) return server;
    const fresh = new Map(server.map((row) => [row.mediaId, row.media]));
    return draft.map((row) => {
      const media = fresh.get(row.mediaId);
      return media && media !== row.media ? { ...row, media } : row;
    });
  }, [draft, server]);
  const dirty = draft !== null && !sameGallery(draft, server);

  const latest = useRef(items);
  const serverRef = useRef(server);
  const onSavedRef = useRef(onSaved);
  useEffect(() => {
    latest.current = items;
    serverRef.current = server;
    onSavedRef.current = onSaved;
  }, [items, server, onSaved]);

  const change = useCallback((next: GalleryItem[]) => {
    latest.current = next;
    setDraft(next);
  }, []);

  const add = useCallback(
    (media: Media[]) => {
      const known = new Set(latest.current.map((i) => i.mediaId));
      const fresh = media
        .filter((m) => !known.has(m.id))
        .map((m) => ({ mediaId: m.id, variantIds: [], media: m }));
      if (fresh.length > 0) change([...latest.current, ...fresh]);
    },
    [change],
  );

  const discard = useCallback(() => {
    latest.current = serverRef.current;
    setDraft(null);
  }, []);

  // The draft stays on screen until the PUT lands, so the order never snaps back in between. A
  // failure keeps it, still unsaved, to save again; the error itself is toasted globally.
  const save = useCallback(async () => {
    const next = latest.current;
    if (sameGallery(next, serverRef.current)) {
      setDraft(null);
      return;
    }
    await mutateAsync(toPutBody(next));
    setSaved(true);
    // A change made while the save ran is a newer draft; keep it.
    setDraft((current) => (current && !sameGallery(current, next) ? current : null));
    onSavedRef.current?.();
  }, [mutateAsync]);

  useSaveBarEntry({
    dirty,
    saving,
    onSave: save,
    onDiscard: discard,
    disabled: !can('products.write'),
  });

  return { productId, query, items, latest, change, add, dirty, saving, save, discard };
}
